import React, { useState } from 'react';
import { Sparkles, MapPin, Info, Plus, Edit3, Trash2, Star, ChevronDown, ChevronUp, FileText, Video, ShoppingBag, ExternalLink, CheckCircle2, Clock, Sun, Moon, ArrowUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface ProfileTabProps {
  theme: ZodiacTheme;
  birthData: {
    date: string;
    time: string;
    place: string;
  };
  setBirthData: (data: {
    date: string;
    time: string;
    place: string;
  }) => void;
  setActiveTab: (tab: string) => void;
}
interface AstroProfile {
  id: string;
  name: string;
  relation: string;
  isPrimary: boolean;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  sign: string;
  signIcon: string;
  moon: string;
  ascendant: string;
  element: string;
}
const mockProfiles: AstroProfile[] = [{
  id: 'p1',
  name: 'Анна',
  relation: 'Я',
  isPrimary: true,
  birthDate: '12 Апр 1990',
  birthTime: '14:30',
  birthPlace: 'Москва, Россия',
  sign: 'Овен',
  signIcon: '♈',
  moon: 'Скорпион',
  ascendant: 'Лев',
  element: 'Огонь'
}, {
  id: 'p2',
  name: 'Максим',
  relation: 'Сын',
  isPrimary: false,
  birthDate: '05 Июн 2015',
  birthTime: '08:15',
  birthPlace: 'Москва, Россия',
  sign: 'Близнецы',
  signIcon: '♊',
  moon: 'Рыбы',
  ascendant: 'Рак',
  element: 'Воздух'
}, {
  id: 'p3',
  name: 'Иван',
  relation: 'Партнер',
  isPrimary: false,
  birthDate: '22 Ноя 1988',
  birthTime: '19:45',
  birthPlace: 'Санкт-Петербург',
  sign: 'Стрелец',
  signIcon: '♐',
  moon: 'Телец',
  ascendant: 'Весы',
  element: 'Огонь'
}];
const profileHistory = [{
  id: 'h1',
  profileId: 'p1',
  type: 'report',
  title: 'Натальная карта',
  date: '12 Окт 2024',
  status: 'ready'
}, {
  id: 'h2',
  profileId: 'p1',
  type: 'report',
  title: 'Соляр 2024',
  date: '10 Окт 2024',
  status: 'ready'
}, {
  id: 'h3',
  profileId: 'p1',
  type: 'consultation',
  title: 'Синастрия',
  date: '15 Окт 2024',
  status: 'scheduled'
}, {
  id: 'h4',
  profileId: 'p1',
  type: 'order',
  title: 'Прогноз на Ноябрь',
  date: '01 Ноя 2024',
  status: 'in_progress'
}, {
  id: 'h5',
  profileId: 'p2',
  type: 'report',
  title: 'Детская натальная карта',
  date: '20 Сен 2024',
  status: 'ready'
}, {
  id: 'h6',
  profileId: 'p3',
  type: 'report',
  title: 'Совместимость',
  date: '15 Сен 2024',
  status: 'ready'
}];
const services = [{
  id: 'natal',
  label: 'Натальная карта',
  icon: '🌟',
  desc: 'Полный разбор личности'
}, {
  id: 'solar',
  label: 'Соляр',
  icon: '☀️',
  desc: 'Прогноз на год'
}, {
  id: 'forecast',
  label: 'Прогноз транзитов',
  icon: '🔮',
  desc: 'Ближайшие события'
}, {
  id: 'compatibility',
  label: 'Совместимость',
  icon: '💞',
  desc: 'Анализ пары'
}];
export function ProfileTab({
  theme,
  birthData,
  setBirthData,
  setActiveTab
}: ProfileTabProps) {
  const {
    showToast
  } = useToast();
  const [activeProfileId, setActiveProfileId] = useState('p1');
  const [isEditing, setIsEditing] = useState(false);
  const [showCharacteristic, setShowCharacteristic] = useState(false);
  const [tempData, setTempData] = useState(birthData);
  const activeProfile = mockProfiles.find(p => p.id === activeProfileId) || mockProfiles[0];
  const history = profileHistory.filter(h => h.profileId === activeProfileId);
  const handleSave = () => {
    setBirthData(tempData);
    setIsEditing(false);
    showToast('Данные профиля сохранены');
  };
  const getHistoryIcon = (type: string) => {
    if (type === 'report') return <FileText className="w-4 h-4 text-indigo-500" data-id="element-18762" />;
    if (type === 'consultation') return <Video className="w-4 h-4 text-emerald-500" data-id="element-18763" />;
    return <ShoppingBag className="w-4 h-4 text-amber-500" data-id="element-18764" />;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'ready') return <Badge variant="success" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18765">
          Готов
        </Badge>;
    if (status === 'scheduled') return <Badge variant="info" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18766">
          Назначено
        </Badge>;
    return <Badge variant="warning" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18767">
        В работе
      </Badge>;
  };
  return <div className="space-y-5" data-id="element-18768">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-18769">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-18770">
          Мой астропрофиль
        </h2>
      </div>

      {/* Profile Switcher */}
      <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-0.5" data-id="element-18771">
        {mockProfiles.map(p => <button key={p.id} onClick={() => setActiveProfileId(p.id)} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors shrink-0 border ${activeProfileId === p.id ? `${theme.bg} ${theme.text} border-current` : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-18772">
            <span className="text-base" data-id="element-18773">
              {p.id === 'p1' ? theme.icon : p.signIcon}
            </span>
            <span data-id="element-18774">{p.name}</span>
            {p.isPrimary && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-18775" />}
          </button>)}
        <button onClick={() => showToast('Добавление нового профиля')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-slate-500 border border-dashed border-slate-300 hover:border-slate-400 hover:text-slate-700 transition-colors shrink-0" data-id="element-18776">
          <Plus className="w-4 h-4" data-id="element-18777" /> Добавить
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-18778">
        {/* LEFT: Profile Card + Astro Data */}
        <div className="lg:col-span-1 space-y-4" data-id="element-18779">
          {/* Profile Card */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-18780">
            <div className={`h-20 bg-gradient-to-br ${theme.gradient} flex items-center justify-center relative`} data-id="element-18781">
              <span className="text-4xl text-white drop-shadow-md" data-id="element-18782">
                {activeProfile.id === 'p1' ? theme.icon : activeProfile.signIcon}
              </span>
              {activeProfile.isPrimary && <span className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full" data-id="element-18783">
                  Основной
                </span>}
            </div>
            <div className="p-4" data-id="element-18784">
              <div className="flex items-center justify-between mb-3" data-id="element-18785">
                <div data-id="element-18786">
                  <h3 className="font-bold text-slate-900" data-id="element-18787">
                    {activeProfile.name}
                  </h3>
                  <span className="text-xs text-slate-500" data-id="element-18788">
                    {activeProfile.relation} · {activeProfile.sign}
                  </span>
                </div>
                <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-18789">
                  {activeProfile.element}
                </Badge>
              </div>

              <div className="space-y-2 text-sm bg-slate-50 rounded-lg p-3 mb-3" data-id="element-18790">
                <div className="flex justify-between" data-id="element-18791">
                  <span className="text-slate-500 text-xs" data-id="element-18792">Дата</span>
                  <span className="font-medium text-slate-900 text-xs" data-id="element-18793">
                    {activeProfile.birthDate}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-18794">
                  <span className="text-slate-500 text-xs" data-id="element-18795">Время</span>
                  <span className="font-medium text-slate-900 text-xs" data-id="element-18796">
                    {activeProfile.birthTime}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-18797">
                  <span className="text-slate-500 text-xs" data-id="element-18798">Место</span>
                  <span className="font-medium text-slate-900 text-xs truncate max-w-[140px]" data-id="element-18799">
                    {activeProfile.birthPlace}
                  </span>
                </div>
              </div>

              <div className="flex gap-2" data-id="element-18800">
                <Button variant="primary" size="sm" className="flex-1 text-xs" onClick={() => showToast(`Профиль "${activeProfile.name}" выбран для заказа`)} data-id="element-18801">
                  Использовать
                </Button>
                <Button variant="outline" size="sm" className="px-2.5" onClick={() => {
                setIsEditing(true);
                setTempData(birthData);
              }} title="Редактировать" data-id="element-18802">
                  <Edit3 className="w-3.5 h-3.5" data-id="element-18803" />
                </Button>
                {!activeProfile.isPrimary && <Button variant="outline" size="sm" className="px-2.5 text-slate-400 hover:text-rose-500" onClick={() => showToast('Профиль удалён')} title="Удалить" data-id="element-18804">
                    <Trash2 className="w-3.5 h-3.5" data-id="element-18805" />
                  </Button>}
              </div>

              {!activeProfile.isPrimary && <button onClick={() => showToast(`"${activeProfile.name}" теперь основной профиль`)} className="w-full mt-2 text-center text-[11px] text-slate-500 hover:text-indigo-600 transition-colors py-1" data-id="element-18806">
                  Сделать основным
                </button>}
            </div>
          </div>

          {/* Astro Data */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-id="element-18807">
            <h4 className="font-bold text-slate-900 text-sm" data-id="element-18808">Астроданные</h4>
            <div className="space-y-2" data-id="element-18809">
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-id="element-18810">
                <Sun className="w-4 h-4 text-amber-500 shrink-0" data-id="element-18811" />
                <div data-id="element-18812">
                  <div className="text-[10px] text-slate-500" data-id="element-18813">Солнце</div>
                  <div className="font-semibold text-sm text-slate-900" data-id="element-18814">
                    {activeProfile.sign}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-id="element-18815">
                <Moon className="w-4 h-4 text-blue-400 shrink-0" data-id="element-18816" />
                <div data-id="element-18817">
                  <div className="text-[10px] text-slate-500" data-id="element-18818">Луна</div>
                  <div className="font-semibold text-sm text-slate-900" data-id="element-18819">
                    {activeProfile.moon}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-id="element-18820">
                <ArrowUp className="w-4 h-4 text-purple-500 shrink-0" data-id="element-18821" />
                <div data-id="element-18822">
                  <div className="text-[10px] text-slate-500" data-id="element-18823">Асцендент</div>
                  <div className="font-semibold text-sm text-slate-900" data-id="element-18824">
                    {activeProfile.ascendant}
                  </div>
                </div>
              </div>
            </div>

            {/* Collapsible characteristic */}
            <button onClick={() => setShowCharacteristic(!showCharacteristic)} className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors pt-1" data-id="element-18825">
              <span data-id="element-18826">Краткая характеристика</span>
              {showCharacteristic ? <ChevronUp className="w-3.5 h-3.5" data-id="element-18827" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-18828" />}
            </button>
            {showCharacteristic && <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-lg" data-id="element-18829">
                Как представитель знака {activeProfile.sign}, вы обладаете
                сильной интуицией и целеустремленностью. Асцендент в{' '}
                {activeProfile.ascendant} усиливает лидерские качества, а Луна в{' '}
                {activeProfile.moon} добавляет глубокую эмоциональность.
              </p>}
          </div>
        </div>

        {/* RIGHT: Edit Form / Services / History */}
        <div className="lg:col-span-2 space-y-5" data-id="element-18830">
          {/* Edit Form */}
          {isEditing && <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4" data-id="element-18831">
              <div className="flex justify-between items-center" data-id="element-18832">
                <h4 className="font-bold text-slate-900 text-sm" data-id="element-18833">
                  Редактирование данных
                </h4>
                <button onClick={() => setIsEditing(false)} className="text-xs text-slate-500 hover:text-slate-700" data-id="element-18834">
                  Отмена
                </button>
              </div>
              <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-xs flex gap-2" data-id="element-18835">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-18836" />
                <span data-id="element-18837">
                  Точное время и место рождения важны для расчёта асцендента и
                  домов.
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-18838">
                <Input label="Дата рождения" type="date" value={tempData.date} onChange={e => setTempData({
              ...tempData,
              date: e.target.value
            })} data-id="element-18839" />
                <Input label="Время рождения" type="time" value={tempData.time} onChange={e => setTempData({
              ...tempData,
              time: e.target.value
            })} data-id="element-18840" />
              </div>
              <Input label="Место рождения" placeholder="Москва, Россия" value={tempData.place} onChange={e => setTempData({
            ...tempData,
            place: e.target.value
          })} icon={<MapPin className="w-4 h-4" data-id="element-18842" />} data-id="element-18841" />
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100" data-id="element-18843">
                <Button variant="ghost" size="sm" onClick={() => {
              setTempData(birthData);
              setIsEditing(false);
            }} data-id="element-18844">
                  Отмена
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave} data-id="element-18845">
                  Сохранить
                </Button>
              </div>
            </div>}

          {/* Services */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-18846">
            <h4 className="font-bold text-slate-900 text-sm mb-3" data-id="element-18847">
              Заказать услугу для {activeProfile.name}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-18848">
              {services.map(s => <button key={s.id} onClick={() => showToast(`Заказ "${s.label}" для профиля "${activeProfile.name}"`)} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors text-center group" data-id="element-18849">
                  <span className="text-xl" data-id="element-18850">{s.icon}</span>
                  <span className="text-xs font-medium text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-18851">
                    {s.label}
                  </span>
                  <span className="text-[10px] text-slate-500 hidden sm:block" data-id="element-18852">
                    {s.desc}
                  </span>
                </button>)}
            </div>
          </div>

          {/* History */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-18853">
            <div className="flex justify-between items-center px-4 py-3 border-b border-slate-100" data-id="element-18854">
              <h4 className="font-bold text-slate-900 text-sm" data-id="element-18855">
                История по профилю
              </h4>
              <button onClick={() => setActiveTab('orders')} className={`text-xs font-medium ${theme.text} hover:underline`} data-id="element-18856">
                Все заказы
              </button>
            </div>
            {history.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-18857">
                {history.map(item => <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors" data-id="element-18858">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0" data-id="element-18859">
                      {getHistoryIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-18860">
                      <div className="font-medium text-sm text-slate-900 truncate" data-id="element-18861">
                        {item.title}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5" data-id="element-18862">
                        <Clock className="w-3 h-3" data-id="element-18863" /> {item.date}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" data-id="element-18864">
                      {getStatusBadge(item.status)}
                      {item.status === 'ready' && <Button variant="ghost" size="sm" className="text-xs px-2 h-7" onClick={() => setActiveTab('reports')} data-id="element-18865">
                          <ExternalLink className="w-3.5 h-3.5" data-id="element-18866" />
                        </Button>}
                    </div>
                  </div>)}
              </div> : <div className="py-8 text-center" data-id="element-18867">
                <FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" data-id="element-18868" />
                <p className="text-xs text-slate-500" data-id="element-18869">
                  Нет истории для этого профиля
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}